import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Activity, Loader2, Sparkles, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const SCORE_COLORS = {
  excellent: { ring: '#10b981', text: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  good:      { ring: '#3b82f6', text: 'text-blue-500',    bg: 'bg-blue-50 dark:bg-blue-950/30' },
  fair:      { ring: '#f59e0b', text: 'text-amber-500',   bg: 'bg-amber-50 dark:bg-amber-950/30' },
  poor:      { ring: '#ef4444', text: 'text-red-500',     bg: 'bg-red-50 dark:bg-red-950/30' },
};

const colorFor = (s) => s >= 85 ? SCORE_COLORS.excellent : s >= 70 ? SCORE_COLORS.good : s >= 50 ? SCORE_COLORS.fair : SCORE_COLORS.poor;

const HealthScoreWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getHealthScore();
        setData(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-5 h-72 flex items-center justify-center">
        <Loader2 size={20} className="text-[#E8613A] animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const c = colorFor(data.overall);
  const radius = 56;
  const stroke = 10;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (data.overall / 100) * circ;

  const benchIcon = data.industry_benchmark?.status === 'above_average' ? TrendingUp
    : data.industry_benchmark?.status === 'below_average' ? TrendingDown : Minus;
  const BenchIcon = benchIcon;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="health-score-widget">
      <div className="flex items-center gap-2 mb-3">
        <Activity size={14} className="text-[#E8613A]" />
        <p className="text-[10px] uppercase tracking-widest text-[#E8613A] font-bold">Företagshälsa</p>
        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Sparkles size={8} /> AI-beräknat</span>
      </div>

      <div className="flex items-center gap-4 mb-4">
        {/* Circular score */}
        <div className="relative w-36 h-36 flex-shrink-0" data-testid="score-circle">
          <svg viewBox="0 0 144 144" className="w-full h-full -rotate-90">
            <circle cx="72" cy="72" r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth={stroke} />
            <circle cx="72" cy="72" r={radius} fill="none" stroke={c.ring} strokeWidth={stroke} strokeLinecap="round"
              strokeDasharray={circ} strokeDashoffset={offset}
              style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(.4,.2,.2,1)' }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className={`text-4xl font-bold ${c.text}`} style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="score-overall">{data.overall}</p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">av 100</p>
          </div>
        </div>

        {/* Rating + bench */}
        <div className="flex-1 min-w-0">
          <p className={`text-lg font-bold ${c.text}`}>{data.rating}</p>
          <div className={`mt-2 ${c.bg} rounded-lg px-3 py-2`}>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">vs Branschen</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <BenchIcon size={12} className={c.text} />
              <p className="text-xs font-bold">{data.industry_benchmark?.label}</p>
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">Snitt {data.industry_benchmark?.industry}: {data.industry_benchmark?.industry_avg_overall}/100</p>
          </div>
        </div>
      </div>

      {/* Dimensions */}
      <div className="space-y-1.5 mt-3 pt-3 border-t border-border">
        {data.dimensions.map(dim => {
          const dc = colorFor(dim.score);
          return (
            <div key={dim.key} className="flex items-center gap-2" data-testid={`dim-${dim.key}`}>
              <span className="text-[11px] text-muted-foreground w-24 flex-shrink-0">{dim.label}</span>
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${dim.score}%`, backgroundColor: dc.ring }} />
              </div>
              <span className={`text-xs font-bold ${dc.text} w-7 text-right`}>{dim.score}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HealthScoreWidget;
